import React, { memo, useCallback, Fragment, useEffect } from 'react';
import { FormArray } from '@core/utils/form';
import { ProcessFormType } from './FormViewModal';
import { useObservable, useNonNilObservable } from '@core/utils/hooks/rxjs';
import { Button, CardText, CardTitle, Table, Collapse } from 'reactstrap';
import { FormHTML } from '@core/components/form/FormHTML';
import { SortableHandle, SortableElement, SortEnd } from 'react-sortable-hoc';
import { SortableList } from '@modules/common/Sortable';
import { R } from '@core/utils/r';
import { TableActions } from '@modules/common/TableActions';
import { DropableImage } from '@modules/common/DropableImage';
import { UploadAPI } from '@business/api/upload';
import { tap } from 'rxjs/operators';
import { FormHTMLInline, FormInput } from '@core/components/form';
import { FormRadioGroup, FormRadio } from '@core/components/form/FormRadio';
import Dropzone from 'react-dropzone';
import { HTMLErrorBoundary } from '@modules/common';
import { HTMLService } from '@business/services';
import { TextBlock } from '@modules/common/TextBlock';
import { useDidMount } from '@core/utils/hooks/react';

interface ProcessItemProps {
  form: ProcessFormType;
  onRemove(index: number): void;
  inIndex: number;
}

const ProcessItem = memo((props: ProcessItemProps) => {
  const { form, onRemove, inIndex } = props;
  const DragHandle = SortableHandle(() => <i className="fas fa-grip-vertical mr-2" />);
  const formValue = useNonNilObservable(form.value$);
  const [isOpen, setIsOpen] = React.useState(false);

  useDidMount(() => {
    if (formValue.type == 'video' && !formValue.embedUrl) {
      setIsOpen(true);
    } else if (formValue.type == 'image' && !formValue.image) {
      setIsOpen(true);
    }
  });

  const onAddFiles = useCallback(
    (newFiles: File[]) => {
      if (newFiles.length == 0) return;
      UploadAPI.upload('library/process', newFiles[0])
        .pipe(
          tap(res => form.patchValue({ image: res.fileUrl, type: 'image', embedUrl: undefined, videoUrl: undefined })),
          tap(() => setIsOpen(false)),
        )
        .subscribe();
    },
    [form],
  );

  const onMediaUpload = useCallback(() => {
    UploadAPI.video(formValue.videoUrl, { autoplay: false })
      .pipe(
        tap(data => {
          form.patchValue({ embedUrl: data.embed, image: undefined });
          setIsOpen(false);
        }),
      )
      .subscribe();
  }, [form, formValue.videoUrl]);

  return (
    <Table className="table table-centered table-nowrap mb-0">
      <thead>
        <tr>
          <th style={{ width: '50%' }}>
            <span>Step {inIndex + 1}</span>
          </th>
          <th style={{ width: '100%' }} className="d-flex justify-content-end align-items-center">
            <DragHandle />
            <TableActions onDelete={() => onRemove(inIndex)} />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style={{ width: '50%' }}>
            {formValue.type == 'image' && <img src={formValue.image} className="img-fluid" />}
            {formValue.type == 'video' && (formValue.embedUrl || '').includes('iframe') && (
              <div style={{ width: '100%', height: 400 }}>
                <HTMLErrorBoundary>{HTMLService.toReact(formValue.embedUrl)}</HTMLErrorBoundary>
              </div>
            )}
            {formValue.type !== 'text' && (
              <CardTitle className="card-title_items mt-4" style={{ cursor: 'pointer' }} onClick={() => setIsOpen(!isOpen)}>
                <div className="text-muted font-size-16">Edit Configuration</div>
                <div className="action">
                  <a className="ml-2">
                    <i className={`bx bx-chevron-${isOpen ? 'up' : 'down'} font-size-22`}></i>
                  </a>
                </div>
              </CardTitle>
            )}
            <Collapse isOpen={isOpen || formValue.type === 'text'}>
              <div className="mt-4">
                <div className="d-flex">
                  <FormRadioGroup control={form.controls.type}>
                    <FormRadio value={'image'} label="Image" />
                    <FormRadio style={{ marginLeft: 10 }} value={'video'} label="Video" />
                    <FormRadio style={{ marginLeft: 10 }} value={'text'} label="Text Only" />
                  </FormRadioGroup>
                </div>
                {formValue.type == 'video' && (
                  <div className="d-flex flex-column">
                    <FormInput control={form.controls.videoUrl} groupProps={{ className: 'd-flex flex-column' }} />
                    <Button color="primary" onClick={onMediaUpload}>
                      Save
                    </Button>
                  </div>
                )}
                {formValue.type == 'image' && (
                  <Dropzone onDrop={onAddFiles} multiple={false} accept="image/*">
                    {({ getRootProps, getInputProps }) => (
                      <div className="dropzone" style={{ minHeight: 150, height: 150 }}>
                        <div style={{ padding: 0 }} className="dz-message needsclick" {...getRootProps()}>
                          <input {...getInputProps()} />
                          <div className="dz-message needsclick">
                            <div className="mb-3">
                              <i className="display-4 text-muted bx bxs-cloud-upload" />
                            </div>
                            <h4>Drop image here</h4>
                          </div>
                        </div>
                      </div>
                    )}
                  </Dropzone>
                )}
              </div>
            </Collapse>
          </td>
          <td style={{ width: '100%', display: 'flex', flexDirection: 'column', whiteSpace: 'initial' }}>
            <div style={{ flex: 1 }}>
              <FormHTMLInline key={form.uid} control={form.controls.content} />
            </div>
          </td>
        </tr>
      </tbody>
    </Table>
  );
});

const ProcessItemSortable = SortableElement(ProcessItem);

export interface ProcessStepProps {
  form: FormArray<ProcessFormType>;
}

export const ProcessStep = memo((props: ProcessStepProps) => {
  const { form } = props;
  const items = useObservable(form.items$, []);
  const formValue = useNonNilObservable(form.value$);
  useEffect(() => {
    if (formValue.length == 0) form.push();
  }, [form, formValue.length]);

  const onSortEnd = useCallback(
    (sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;
      const newItems = R.move(formValue, oldIndex, newIndex);
      form.setValue(newItems);
    },
    [form, formValue],
  );

  return (
    <div>
      {/* <div className="mb-2">
        <div className="d-flex align-items-start ">
          <div style={{ flex: 1, height: '100%', marginRight: 20 }}>
            <CardTitle>Example</CardTitle>
          </div>
          <div style={{ flex: 1 }}>
            <CardTitle>Process Steps</CardTitle>
          </div>
          <div className="d-flex ml-4 align-items-center" style={{ width: 50 }}></div>
        </div>
        <hr />
      </div> */}
      <TextBlock classNameTitle="font-weight-bold font-size-20" className="mb-4" title={`Process Steps`}>
        <div></div>
      </TextBlock>

      <SortableList lockAxis="y" distance={2} helperClass="z5000" onSortEnd={onSortEnd} useDragHandle>
        {items.map((inForm, index) => (
          <ProcessItemSortable
            index={index}
            inIndex={index}
            onRemove={index => {
              form.removeAt(index);
            }}
            key={inForm.uid}
            form={inForm}
          />
        ))}
      </SortableList>
      <div className="d-flex justify-content-end mr-2">
        <Button color="primary" className="mt-2" onClick={() => form.push()}>
          Add
        </Button>
      </div>
    </div>
  );
});
